import React from "react";
import { LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export const Header = ({ activeTab, onTabChange }) => {
  const { user, logout } = useAuth();
  return (
    <header className="border-b-2 border-dotr-ink bg-dotr-bg" data-testid="app-header">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-5 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <img
            src="/dotr-logo.png"
            alt="Dum on the Run logo"
            className="w-14 h-14 md:w-16 md:h-16 object-contain border-2 border-dotr-ink bg-white shrink-0"
            data-testid="brand-logo"
          />
          <div className="leading-tight">
            <div className="font-heading font-semibold text-xl tracking-tight">DUM ON THE RUN<sup className="text-xs ml-0.5">®</sup></div>
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-dotr-inkmuted">
              ORDER OPERATIONS · DOTR
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <nav className="flex gap-0" role="tablist" aria-label="Sections">
            <button
              role="tab"
              aria-selected={activeTab === "eftpos"}
              data-testid="tab-eftpos"
              onClick={() => onTabChange("eftpos")}
              className={`px-5 md:px-7 py-3 font-heading uppercase tracking-[0.1em] text-sm border-2 border-dotr-ink transition-all ${
                activeTab === "eftpos"
                  ? "bg-dotr-primary text-white shadow-brutalSm"
                  : "bg-transparent text-dotr-ink hover:bg-dotr-muted"
              }`}
            >
              Eftpos
            </button>
            <button
              role="tab"
              aria-selected={activeTab === "new-order"}
              data-testid="tab-new-order"
              onClick={() => onTabChange("new-order")}
              className={`px-5 md:px-7 py-3 font-heading uppercase tracking-[0.1em] text-sm border-2 border-l-0 border-dotr-ink transition-all ${
                activeTab === "new-order"
                  ? "bg-dotr-ink text-white shadow-brutalSm"
                  : "bg-transparent text-dotr-ink hover:bg-dotr-muted"
              }`}
            >
              New Order
            </button>
            <button
              role="tab"
              aria-selected={activeTab === "admin"}
              data-testid="tab-admin"
              onClick={() => onTabChange("admin")}
              className={`px-5 md:px-7 py-3 font-heading uppercase tracking-[0.1em] text-sm border-2 border-l-0 border-dotr-ink transition-all ${
                activeTab === "admin"
                  ? "bg-dotr-ink text-white shadow-brutalSm"
                  : "bg-transparent text-dotr-ink hover:bg-dotr-muted"
              }`}
            >
              Admin Console
            </button>
            {user?.role === "admin" && (
              <button
                role="tab"
                aria-selected={activeTab === "pricebook"}
                data-testid="tab-pricebook"
                onClick={() => onTabChange("pricebook")}
                className={`px-5 md:px-7 py-3 font-heading uppercase tracking-[0.1em] text-sm border-2 border-l-0 border-dotr-ink transition-all ${
                  activeTab === "pricebook"
                    ? "bg-dotr-ink text-white shadow-brutalSm"
                    : "bg-transparent text-dotr-ink hover:bg-dotr-muted"
                }`}
              >
                Price Book
              </button>
            )}
            {user?.role === "admin" && (
              <button
                role="tab"
                aria-selected={activeTab === "invoices"}
                data-testid="tab-invoices"
                onClick={() => onTabChange("invoices")}
                className={`px-5 md:px-7 py-3 font-heading uppercase tracking-[0.1em] text-sm border-2 border-l-0 border-dotr-ink transition-all ${
                  activeTab === "invoices"
                    ? "bg-dotr-ink text-white shadow-brutalSm"
                    : "bg-transparent text-dotr-ink hover:bg-dotr-muted"
                }`}
              >
                Invoices
              </button>
            )}
            {user?.role === "admin" && (
              <button
                role="tab"
                aria-selected={activeTab === "franchise-admin"}
                data-testid="tab-franchise-admin"
                onClick={() => onTabChange("franchise-admin")}
                className={`px-5 md:px-7 py-3 font-heading uppercase tracking-[0.1em] text-sm border-2 border-l-0 border-dotr-ink transition-all ${
                  activeTab === "franchise-admin"
                    ? "bg-dotr-ink text-white shadow-brutalSm"
                    : "bg-transparent text-dotr-ink hover:bg-dotr-muted"
                }`}
              >
                Franchise Admin
              </button>
            )}
            {user?.role === "admin" && (
              <button
                role="tab"
                aria-selected={activeTab === "franchise"}
                data-testid="tab-franchise"
                onClick={() => onTabChange("franchise")}
                className={`px-5 md:px-7 py-3 font-heading uppercase tracking-[0.1em] text-sm border-2 border-l-0 border-dotr-ink transition-all ${
                  activeTab === "franchise"
                    ? "bg-dotr-ink text-white shadow-brutalSm"
                    : "bg-transparent text-dotr-ink hover:bg-dotr-muted"
                }`}
              >
                Franchise Settings
              </button>
            )}
          </nav>

          {user && (
            <div className="flex items-center gap-2" data-testid="user-pill">
              <div className="hidden md:flex flex-col items-end leading-tight">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted">
                  {user.role === "admin" ? "Admin · All Spokes" : "Signed in"}
                </span>
                <span className="font-heading font-semibold text-sm uppercase tracking-tight">
                  {user.role === "admin" ? user.display_name : user.spoke}
                </span>
              </div>
              <button
                type="button"
                onClick={logout}
                data-testid="btn-logout"
                className="inline-flex items-center gap-1 px-3 py-2 border-2 border-dotr-ink bg-white hover:bg-dotr-primary hover:text-white transition-colors font-heading uppercase tracking-wider text-xs"
                aria-label="Log out"
              >
                <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
